import React from 'react';
import styled, { css } from 'styled-components';

import { SUPPORT_ICONS } from '../components/supportStyles';
import { SupportAction, SupportActionIcon } from '../types';
import { responseTone, responseType, rsColor, rsFor, rsv } from './responseScale';

type ButtonLook = 'primary' | 'secondary' | 'link' | 'icon';

/** An icon name the client does not ship draws no glyph rather than failing the render. */
export const ResponseIcon: React.FC<{ icon?: SupportActionIcon | null }> = ({ icon }) => {
  const Icon = icon === undefined || icon === null ? undefined : SUPPORT_ICONS[icon];
  if (Icon === undefined) {
    return null;
  }
  return (
    <span aria-hidden="true" data-icon={icon}>
      <Icon />
    </span>
  );
};

const lookCss = (look: ButtonLook, fill: string) => {
  switch (look) {
    // 6265:4608: 44 tall under a transparent 1px stroke.
    case 'primary':
      return css`
        padding: ${rsv.space('button')};
        border: 1px solid transparent;
        border-radius: ${rsv.radius('button')}px;
        background: ${fill};
        color: ${rsColor.white};
        ${responseType('button')}
      `;
    // 6265:4824.
    case 'secondary':
      return css`
        padding: ${rsv.space('buttonSecondary')};
        border: 1px solid ${rsColor.border};
        border-radius: ${rsv.radius('button')}px;
        background: ${rsColor.inset};
        color: ${rsColor.text};
        ${responseType('button')}
      `;
    // 6604:1791: the glyph leads the label.
    case 'link':
      return css`
        padding: 0;
        border: none;
        background: transparent;
        color: ${rsColor.link};
        ${responseType('link')}

        > * + * {
          margin-left: ${rsv.space('linkIconGap')}px;
        }

        .anticon {
          font-size: ${rsv.size('linkIcon')}px;
        }

        &:hover:not(:disabled) > span:not([aria-hidden]) {
          text-decoration: underline;
        }
      `;
    // 6265:4768 / 6424:11893: 34 x 34, an 18 glyph under 8 padding.
    default:
      return css`
        box-sizing: border-box;
        justify-content: center;
        width: ${rsv.size('iconButton')}px;
        height: ${rsv.size('iconButton')}px;
        padding: ${props =>
          (rsFor(props).size.iconButton - rsFor(props).size.iconButtonGlyph) / 2}px;
        border: none;
        border-radius: ${rsv.radius('iconButton')}px;
        background: transparent;
        color: ${rsColor.muted};

        .anticon {
          font-size: ${rsv.size('iconButtonGlyph')}px;
        }

        &:hover:not(:disabled) {
          background: ${rsColor.inset};
        }
      `;
  }
};

const Button = styled.button<{ $look: ButtonLook; $fill: string }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  cursor: pointer;
  white-space: nowrap;

  > * + * {
    margin-left: ${rsv.space('buttonIconGap')}px;
  }

  .anticon {
    font-size: ${rsv.size('buttonIcon')}px;
  }

  ${({ $look, $fill }) => lookCss($look, $fill)}

  &:focus-visible {
    outline: 2px solid ${rsColor.link};
    outline-offset: 2px;
  }

  /* 6473:5493: a greyed button is a solid fill, not a faded one. */
  &:disabled {
    cursor: not-allowed;
    ${({ $look }) =>
      $look === 'primary' || $look === 'secondary'
        ? css`
            border-color: ${rsColor.disabled};
            background: ${rsColor.disabled};
            color: ${rsColor.white};
          `
        : css`
            color: ${rsColor.muted};
          `}
  }
`;

export interface IResponseActionButtonProps {
  action: SupportAction;
  onAction: (action: SupportAction) => void;
  /** Forces the inert look on top of the payload's own `disabled`. */
  disabled?: boolean;
  /** Draws the glyph alone; the label becomes the accessible name. */
  iconOnly?: boolean;
  className?: string;
}

export const ResponseActionButton: React.FC<IResponseActionButtonProps> = ({
  action,
  onAction,
  disabled = false,
  iconOnly = false,
  className
}) => {
  const inert = disabled || action.disabled === true;
  const look: ButtonLook = iconOnly ? 'icon' : action.kind;
  // 6604:1791 trails its arrow; every other glyph leads its label (6604:1144).
  const trailing = !iconOnly && action.icon === 'arrow';
  const fill = action.tone ? responseTone(action.tone).ink : rsColor.primary;
  return (
    <Button
      type="button"
      className={className}
      $look={look}
      $fill={fill}
      disabled={inert}
      aria-label={iconOnly ? action.label : undefined}
      title={iconOnly ? action.label : undefined}
      data-action-id={action.id}
      data-look={look}
      data-testid="orbit-response-action"
      onClick={() => onAction(action)}
    >
      {trailing ? null : <ResponseIcon icon={action.icon} />}
      {iconOnly ? null : <span>{action.label}</span>}
      {trailing ? <ResponseIcon icon={action.icon} /> : null}
    </Button>
  );
};

// Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5).
export const ResponseActionRow = styled.div<{ $align?: 'start' | 'end' }>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: ${({ $align }) => ($align === 'end' ? 'flex-end' : 'flex-start')};
  margin: ${rsv.actionsTop('buttonRowTop')}px 0 0 -${rsv.space('buttonGap')}px;

  > * {
    margin: ${rsv.space('buttonGap')}px 0 0 ${rsv.space('buttonGap')}px;
  }
`;

export interface IResponseActionsProps {
  actions: SupportAction[] | null | undefined;
  onAction: (action: SupportAction) => void;
  align?: 'start' | 'end';
  /** Greys every action, e.g. while the block is processing. */
  disabled?: boolean;
  className?: string;
}

/** The row of a block's actions; draws nothing when the payload sends none. */
export const ResponseActions: React.FC<IResponseActionsProps> = ({
  actions,
  onAction,
  align,
  disabled,
  className
}) => {
  if (!actions?.length) {
    return null;
  }
  return (
    <ResponseActionRow $align={align} className={className} data-testid="orbit-response-actions">
      {actions.map(action => (
        <ResponseActionButton
          key={action.id}
          action={action}
          onAction={onAction}
          disabled={disabled}
        />
      ))}
    </ResponseActionRow>
  );
};
